// rows for which LEs might want to display an ENGLISH translation
import { ALTERNATIVE_FIELD_IDS, COORDINATES_COMPARISON_FIELD_ID } from '../constants';
import { environment } from '../../../../environments/environment';
import { CorrespondingBeValue, CorrespondingCheckCircleValue, CorrespondingColumnHeading, CorrespondingInfoMenuContent, DifferenceActions } from '../enums/difference-actions';
import { ColumnVisibilityModel } from './bls-gmb-differences-local-store.service';
import { getFullNameForFieldID } from '../location-utils';
import { BlsGmbDifference } from '../../../models/locations-models/bls-gmb-difference';
import { prepareGmbDiffMap } from '../diff-utils';
import { Location } from '../../../models/locations-models/location';
import * as memoizee from 'memoizee';

export const TRANSLATABLE_ROWS = ['address', 'businessName'].concat(ALTERNATIVE_FIELD_IDS['businessName']);

// defining the table column order
export const DISPLAYED_COLUMNS: string[] = [
  'fieldId',                 // LSC Field

  // column 1 (checkbox + actual diff value)
  'updateGmb',               // Actions.updateGmb
  'blsValue',                // CorrespondingBeValue[Actions.updateGmb]

  // column 2 (checkbox + actual diff value)
  'acceptDifference',        // Actions.acceptDifference
  'gmbValue',                // CorrespondingBeValue[Actions.acceptDifference]
].concat(
    // TODO careful -- the prod env flag must be false
    environment.activeFeatures.dev.showGoogleUpdateValueColumn
        ? [
          // column 3 (checkbox + actual diff value)
          'acceptGoogleUpdateValue',  // Actions.acceptGoogleUpdateValue
          'googleUpdateValue',        // CorrespondingBeValue[Actions.acceptGoogleUpdateValue]
        ] : [],
);

// For rendering the UI columns corresponding to applicable `Actions`
export const getColumnDefinitions = (getSelectAllBtnTextCB: (action: DifferenceActions,
                                                             differences: BlsGmbDifference[],
                                                             location: Location) => string,
                                     toggleColumnCB: (action: DifferenceActions,
                                                      columnVisibility: ColumnVisibilityModel) => void) => {
  return [].concat([
    DifferenceActions.updateGmb,
    DifferenceActions.acceptDifference,
    DifferenceActions.acceptGoogleUpdateValue,
  ])
      .map((action: DifferenceActions) => {
        return {
          action: action,

          // only the LSC column is "blacklist-able"
          isBlacklistAble: memoizee((action: DifferenceActions) => action === DifferenceActions.updateGmb),

          visibility: {
            shouldShowToggleBtn: memoizee((action: DifferenceActions) => environment.activeFeatures.dev.showGoogleUpdateValueColumn
                && action !== DifferenceActions.updateGmb),
            // hiding the 2nd or 3rd columns if one is already hidden since
            // there's hardly a use case for hiding both at the same time
            shouldDisableToggleBtn: memoizee((action: DifferenceActions, columnVisibility: ColumnVisibilityModel) => {
              switch (action) {
                case DifferenceActions.updateGmb:
                  // returning disabled but we don't really care b/c *ngIf will hide this anyways
                  return true;
                case DifferenceActions.acceptDifference:
                  // let's disable the toggle if the opposite column is already shown
                  return !columnVisibility[DifferenceActions.acceptGoogleUpdateValue];
                case DifferenceActions.acceptGoogleUpdateValue:
                  // same here
                  return !columnVisibility[DifferenceActions.acceptDifference];
              }
            }),
            toggleColumn: toggleColumnCB,
            isVisible: (columnVisibility: ColumnVisibilityModel) => columnVisibility[action],
            // https://fonts.google.com/icons?icon.query=visibility
            getIconName: memoizee((columnVisibility: ColumnVisibilityModel) => columnVisibility[action] ? 'visibility_off_outline' : 'visibility_outline'),
          },
          checkbox: {
            checkCircleModelName: CorrespondingCheckCircleValue[action],
            // Only revalidating GBP coordinates possible so let's hide the other check circle
            // Note -- only the `visibility` is off to preserve the margins.
            // Implements BLS-660.
            isCheckCircleHidden: memoizee((fieldId: string, action: DifferenceActions) => {
              return getFullNameForFieldID(fieldId).toLowerCase().includes('coordinate') &&
                  action === DifferenceActions.updateGmb;
            }),
          },
          infoMenu: {
            content: CorrespondingInfoMenuContent[action],
          },
          display: {
            backendDifferenceName: CorrespondingBeValue[action],
            heading: CorrespondingColumnHeading[action],

            getMapConfig: memoizee((action: DifferenceActions, difference: BlsGmbDifference) => {
              // display the map only "within" the 1st checkbox-column (the `updateGmb` / `LSC` column)
              if (action === DifferenceActions.updateGmb
                  // and only for the lat/lng column of interest
                  && difference.fieldId === COORDINATES_COMPARISON_FIELD_ID
                  // GMB location latLng is not null/empty
                  && difference.gmbValue?.length !== 0) {
                return prepareGmbDiffMap(difference);
              }

              return null;
            }),

            getSelectAllBtnText: getSelectAllBtnTextCB,
          },
        };
      });
};
